/**
 * New / Edit activity dialog for the Marketing Calendar.
 * Extracted from ClientCalendarPage for maintainability.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, Trash2, Upload, Paperclip, X, Check, Clock, ChevronDown, ChevronUp,
  MessageSquare, Eye, Wand2, Info,
  ExternalLink as ExternalLink2,
} from "lucide-react";
import { PlatformIcon, PLATFORMS } from "@/components/PlatformIcon";
import { contentTypesFor, specFor } from "@/lib/contentSpecs";
import {
  STATUS_META, isoDate, combineDateTime, splitDateTime,
} from "@/components/calendar/calendarUtils";


export function ActivityDialog({ clientId, mode, activity, defaultDay, onClose, onSaved, onSilentReload, isAdmin, canAiStudio = false, navigate }) {
  const isNew = mode === "new";
  const initial = useMemo(() => {
    if (activity) {
      const { day, time } = splitDateTime(activity.scheduled_at);
      return {
        title: activity.title || "",
        platform: activity.platform || "generic",
        content_type: activity.content_type || "",
        business_unit_id: activity.business_unit_id || "",
        description: activity.description || "",
        post_content: activity.post_content || "",
        day, time,
        team_member_id: activity.team_member_id || "",
        signoff_user_id: activity.signoff_user_id || "",
        estimated_hours: activity.estimated_hours ?? 0.25,
        status: activity.status || "planned",
      };
    }
    return {
      title: "", platform: "facebook",
      content_type: "post",
      business_unit_id: "",
      description: "", post_content: "",
      day: defaultDay || isoDate(new Date()),
      time: "09:00",
      team_member_id: "", signoff_user_id: "",
      estimated_hours: 0.25,  // default to 15 minutes
      status: "planned",
    };
  }, [activity, defaultDay]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState(activity?.files || []);
  const [refLinks, setRefLinks] = useState(activity?.reference_links || []);
  const [uploading, setUploading] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkTitle, setNewLinkTitle] = useState("");
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [history, setHistory] = useState(activity?.history || []);
  const [notes, setNotes] = useState(activity?.notes || []);
  const [views, setViews] = useState(activity?.views || []);
  const [newNote, setNewNote] = useState("");
  const [postingNote, setPostingNote] = useState(false);
  // "single" (existing flow) or "batch" (new — fan out across many platforms).
  // Only meaningful for new activities; edits are always single.
  const [createMode, setCreateMode] = useState("single");
  const [batchPlatforms, setBatchPlatforms] = useState(() => new Set(["facebook"]));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Whenever the platform changes, make sure the content_type still belongs
  // to that platform's catalogue. Default to the first option if not.
  useEffect(() => {
    const types = contentTypesFor(form.platform);
    if (!types.find((t) => t.key === form.content_type)) {
      setForm((f) => ({ ...f, content_type: types[0]?.key || "" }));
    }
  }, [form.platform, form.content_type]);

  // ── Stamp a view on open (admin or client). The backend caps the array
  //    at 200 entries so this never grows unbounded.
  const viewRecordedRef = useRef(false);
  useEffect(() => {
    if (isNew || viewRecordedRef.current || !activity?.activity_id) return;
    viewRecordedRef.current = true;
    api.post(`/calendar/activities/${activity.activity_id}/view`)
      .then(() => {
        setViews((v) => [...v, {
          at: new Date().toISOString(), by_name: "you (just now)",
        }]);
      })
      .catch(() => { /* best-effort, no toast */ });
  }, [activity?.activity_id, isNew]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/team-members", { params: { active_only: true } })
        .then((r) => setTeam(r.data || []))
        .catch(() => setTeam([]));
    }
    api.get(`/clients/${clientId}/contacts`)
      .then((r) => setContacts(r.data || []))
      .catch(() => setContacts([]));
    api.get(`/clients/${clientId}/business-units`)
      .then((r) => setProperties(r.data || []))
      .catch(() => setProperties([]));
  }, [clientId, isAdmin]);

  const togglePlatform = (key) => {
    setBatchPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (isNew && createMode === "batch") {
        if (batchPlatforms.size === 0) {
          toast.error("Pick at least one platform"); setSaving(false); return;
        }
        const { data } = await api.post(
          `/clients/${clientId}/calendar/activities/batch`,
          {
            platforms: Array.from(batchPlatforms),
            title: form.title.trim(),
            business_unit_id: form.business_unit_id || null,
            description: form.description,
            post_content: form.post_content,
            scheduled_at: combineDateTime(form.day, form.time),
            team_member_id: form.team_member_id || null,
            signoff_user_id: form.signoff_user_id || null,
            estimated_hours: form.estimated_hours === "" ? null : Number(form.estimated_hours),
            status: form.status,
          },
        );
        toast.success(`Scheduled ${data?.created || batchPlatforms.size} activities`);
        onSaved?.();
        return;
      }
      const payload = {
        title: form.title.trim(),
        platform: form.platform,
        content_type: form.content_type || null,
        business_unit_id: form.business_unit_id || null,
        description: form.description,
        post_content: form.post_content,
        scheduled_at: combineDateTime(form.day, form.time),
        team_member_id: form.team_member_id || null,
        signoff_user_id: form.signoff_user_id || null,
        estimated_hours: form.estimated_hours === "" ? null : Number(form.estimated_hours),
        status: form.status,
      };
      if (isNew) {
        await api.post(`/clients/${clientId}/calendar/activities`, payload);
        toast.success("Activity scheduled");
      } else {
        await api.patch(`/calendar/activities/${activity.activity_id}`, payload);
        toast.success("Activity updated");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${activity.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/calendar/activities/${activity.activity_id}`);
      toast.success("Activity deleted");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const onUploadFiles = async (fileList, kind = "attachment") => {
    if (!activity?.activity_id) {
      toast.error("Save the activity first before attaching files.");
      return;
    }
    const setter = kind === "reference" ? setUploadingRef : setUploading;
    setter(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(
          `/calendar/activities/${activity.activity_id}/files?kind=${kind}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } },
        );
        setFiles((prev) => [...prev, data]);
      }
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setter(false); }
  };

  const addRefLink = async () => {
    const url = newLinkUrl.trim();
    if (!url) return;
    try {
      const { data } = await api.post(
        `/calendar/activities/${activity.activity_id}/reference-links`,
        { url, title: newLinkTitle.trim() || null },
      );
      setRefLinks((prev) => [...prev, data]);
      setNewLinkUrl(""); setNewLinkTitle(""); setShowAddLink(false);
      toast.success("Reference link added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add link");
    }
  };

  const removeRefLink = async (linkId) => {
    try {
      await api.delete(`/calendar/activities/${activity.activity_id}/reference-links/${linkId}`);
      setRefLinks((prev) => prev.filter((l) => l.link_id !== linkId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  };

  const removeFile = async (fileId) => {
    try {
      await api.delete(`/calendar/activities/${activity.activity_id}/files/${fileId}`);
      setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const transition = async (target, note = null) => {
    try {
      const { data } = await api.post(
        `/calendar/activities/${activity.activity_id}/transition`,
        { status: target, note },
      );
      set("status", data.status);
      setHistory(data.history || []);
      toast.success(`Status → ${STATUS_META[target]?.label || target}`);
      // Refresh the parent list silently but keep the dialog open so the
      // user can keep working on this item.
      onSilentReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    }
  };

  const postNote = async () => {
    const body = (newNote || "").trim();
    if (!body) return;
    setPostingNote(true);
    try {
      const { data } = await api.post(
        `/calendar/activities/${activity.activity_id}/notes`,
        { note: body },
      );
      setNotes((prev) => [...prev, data]);
      setNewNote("");
      onSilentReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post note");
    } finally { setPostingNote(false); }
  };

  // Client-portal sign-off actions for ready_for_signoff items.
  const clientCanSignOff = !isAdmin
    && !isNew
    && form.status === "ready_for_signoff";

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight flex items-center gap-2">
            {createMode === "batch" && isNew
              ? <Wand2 className="h-5 w-5 text-[#002FA7]" />
              : <PlatformIcon platform={form.platform} size={20} />}
            {isNew
              ? (createMode === "batch"
                  ? `New multi-platform activity · ${batchPlatforms.size} channel${batchPlatforms.size === 1 ? "" : "s"}`
                  : "New activity")
              : (activity?.title || "Activity")}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? (createMode === "batch"
                  ? "We'll create one activity per selected platform — all sharing this title, brief, post copy, schedule and ownership."
                  : "Schedule a social post, blog article, email campaign or anything else.")
              : `Last updated ${activity?.updated_at ? new Date(activity.updated_at).toLocaleString("en-AU") : ""}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {isNew && (
            <div className="flex items-center gap-1.5 rounded-sm border border-zinc-200 bg-zinc-50 p-1 self-start w-fit"
              data-testid="activity-mode-toggle">
              <button type="button"
                onClick={() => setCreateMode("single")}
                data-testid="mode-single"
                className={`px-3 py-1 rounded-sm text-[11px] font-mono uppercase tracking-wider transition-colors ${
                  createMode === "single"
                    ? "bg-[#002FA7] text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900"
                }`}>
                Single platform
              </button>
              <button type="button"
                onClick={() => setCreateMode("batch")}
                data-testid="mode-batch"
                className={`px-3 py-1 rounded-sm text-[11px] font-mono uppercase tracking-wider transition-colors ${
                  createMode === "batch"
                    ? "bg-[#002FA7] text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900"
                }`}>
                Multi-platform · {batchPlatforms.size} selected
              </button>
            </div>
          )}

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-7">
              <Label className="label-overline">Title</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)}
                disabled={!isAdmin}
                className="mt-1 rounded-sm h-9 text-sm"
                placeholder="e.g. Spring promo announcement"
                data-testid="activity-title" />
            </div>
            <div className="col-span-6 md:col-span-3">
              <Label className="label-overline">Platform</Label>
              {createMode === "batch" ? (
                <div className="mt-1 h-9 rounded-sm border border-zinc-200 bg-zinc-50
                  px-2 flex items-center text-[11px] font-mono text-zinc-500"
                  data-testid="activity-platform-batch-indicator">
                  {batchPlatforms.size} platform{batchPlatforms.size === 1 ? "" : "s"} picked below
                </div>
              ) : (
                <Select value={form.platform} onValueChange={(v) => set("platform", v)}
                  disabled={!isAdmin}>
                  <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="activity-platform">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        <span className="inline-flex items-center gap-2">
                          <PlatformIcon platform={p.key} size={14} /> {p.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="col-span-6 md:col-span-2">
              <Label className="label-overline">Status</Label>
              <div className="mt-1">
                <Badge className="rounded-sm" style={{
                  background: STATUS_META[form.status]?.bg,
                  color: STATUS_META[form.status]?.color,
                  border: `1px solid ${STATUS_META[form.status]?.color}33`,
                }}>
                  {STATUS_META[form.status]?.label}
                </Badge>
              </div>
            </div>
          </div>

          {/* Content type picker + Content Guidance Panel (single mode) */}
          {createMode === "single" && (
            <>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 md:col-span-7">
                  <Label className="label-overline">Content type</Label>
                  <Select value={form.content_type || ""}
                    onValueChange={(v) => set("content_type", v)}
                    disabled={!isAdmin}>
                    <SelectTrigger className="mt-1 rounded-sm h-9 text-sm"
                      data-testid="activity-content-type">
                      <SelectValue placeholder="Pick the format" />
                    </SelectTrigger>
                    <SelectContent>
                      {contentTypesFor(form.platform).map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 md:col-span-5 flex items-end">
                  <Button type="button" variant="outline"
                    disabled={!canAiStudio || !activity?.activity_id}
                    onClick={async () => {
                      const aid = activity?.activity_id;
                      if (!aid) {
                        toast.info("Save the activity first — then AI Studio has a brief to work from.");
                        return;
                      }
                      // Persist any unsaved edits (brief, title…) so AI
                      // Studio works from what's on screen, not a stale save.
                      if (isAdmin) {
                        try {
                          await api.patch(`/calendar/activities/${aid}`, {
                            title: form.title.trim() || undefined,
                            description: form.description,
                            post_content: form.post_content,
                          });
                        } catch { /* best-effort — still open the studio */ }
                      }
                      navigate(`/clients/${clientId}/calendar/${aid}/ai-studio`);
                    }}
                    title={!canAiStudio
                      ? "Your admin hasn't enabled AI Studio for this contact yet."
                      : !activity?.activity_id
                        ? "Save this activity first so AI Studio can use the brief."
                        : "Open the step-by-step content generator"}
                    className="rounded-sm h-9 w-full text-sm
                      bg-gradient-to-r from-[#002FA7]/5 to-purple-50
                      border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/10"
                    data-testid="activity-ai-generate">
                    <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                    Create with AI
                    <span className="ml-auto text-[9px] font-mono opacity-60">studio</span>
                  </Button>
                </div>
              </div>

              <ContentGuidancePanel
                platform={form.platform}
                contentType={form.content_type} />
            </>
          )}

          {/* Multi-platform picker (batch mode) */}
          {createMode === "batch" && isNew && (
            <div className="rounded-sm border border-[#002FA7]/15 bg-[#002FA7]/[0.04] p-3"
              data-testid="activity-batch-platforms">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="inline-flex items-center gap-2">
                  <span className="label-overline text-[#002FA7]">
                    Pick the platforms — one activity per channel
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
                  <button type="button"
                    onClick={() => setBatchPlatforms(new Set(PLATFORMS.filter((p) => p.key !== "generic").map((p) => p.key)))}
                    className="hover:text-[#002FA7] underline"
                    data-testid="batch-select-all">
                    Select all
                  </button>
                  <span>·</span>
                  <button type="button"
                    onClick={() => setBatchPlatforms(new Set())}
                    className="hover:text-[#002FA7] underline"
                    data-testid="batch-clear">
                    Clear
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                {PLATFORMS.filter((p) => p.key !== "generic").map((p) => {
                  const selected = batchPlatforms.has(p.key);
                  return (
                    <label key={p.key}
                      className={`flex items-center gap-2 rounded-sm border px-2 py-1.5 cursor-pointer transition-all ${
                        selected
                          ? "bg-white border-[#002FA7] ring-1 ring-[#002FA7]"
                          : "bg-white/60 border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`batch-platform-${p.key}`}>
                      <input type="checkbox" checked={selected}
                        onChange={() => togglePlatform(p.key)}
                        className="rounded-sm accent-[#002FA7]" />
                      <PlatformIcon platform={p.key} size={14} />
                      <span className="text-[12px] truncate">{p.label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] font-mono text-zinc-500">
                A separate activity will be created for each ticked platform — sharing the title,
                description, post copy, schedule and ownership you fill in above. Edit each
                activity individually afterwards to set platform-specific content types and assets.
              </div>
            </div>
          )}

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-6">
              <Label className="label-overline">Property</Label>
              <Select value={form.business_unit_id || "_none"}
                onValueChange={(v) => set("business_unit_id", v === "_none" ? "" : v)}
                disabled={!isAdmin}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="activity-property">
                  <SelectValue placeholder="Which property is this for?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Whole client (no specific property) —</SelectItem>
                  {properties.map((p) => (
                    <SelectItem key={p.business_unit_id} value={p.business_unit_id}>
                      {p.name}
                      {p.website_url ? ` · ${p.website_url.replace(/^https?:\/\//, "")}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-6 md:col-span-3">
              <Label className="label-overline">Date</Label>
              <Input type="date" value={form.day}
                disabled={!isAdmin}
                onChange={(e) => set("day", e.target.value)}
                className="mt-1 rounded-sm h-9 text-sm font-mono"
                data-testid="activity-date" />
            </div>
            <div className="col-span-6 md:col-span-3">
              <Label className="label-overline">Time</Label>
              <Input type="time" value={form.time}
                disabled={!isAdmin}
                onChange={(e) => set("time", e.target.value)}
                className="mt-1 rounded-sm h-9 text-sm font-mono"
                data-testid="activity-time" />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-6">
              <Label className="label-overline">
                Estimated time to complete
              </Label>
              <div className="mt-1 flex items-stretch rounded-sm border border-zinc-200 overflow-hidden">
                <button type="button"
                  disabled={!isAdmin}
                  onClick={() => {
                    const cur = Number(form.estimated_hours) || 0;
                    set("estimated_hours", Math.max(0.25, +(cur - 0.25).toFixed(2)));
                  }}
                  data-testid="activity-hours-down"
                  className="px-2 hover:bg-zinc-100 border-r border-zinc-200 text-zinc-600 disabled:opacity-40">
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <Input type="number" step="0.25" min="0.25"
                  value={form.estimated_hours}
                  disabled={!isAdmin}
                  onChange={(e) => set("estimated_hours", e.target.value)}
                  className="border-0 rounded-none h-9 text-sm font-mono text-center"
                  data-testid="activity-hours" />
                <button type="button"
                  disabled={!isAdmin}
                  onClick={() => {
                    const cur = Number(form.estimated_hours) || 0;
                    set("estimated_hours", +(cur + 0.25).toFixed(2));
                  }}
                  data-testid="activity-hours-up"
                  className="px-2 hover:bg-zinc-100 border-l border-zinc-200 text-zinc-600 disabled:opacity-40">
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <span className="px-3 text-[11px] font-mono text-zinc-500 self-center
                  border-l border-zinc-200">
                  hrs
                </span>
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1">
                Minimum 15 minutes · adjust in 15-min steps
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-6">
              <Label className="label-overline">Team member responsible</Label>
              <Select value={form.team_member_id || "_none"}
                onValueChange={(v) => set("team_member_id", v === "_none" ? "" : v)}
                disabled={!isAdmin}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="activity-team-member">
                  <SelectValue placeholder="Pick a team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Unassigned —</SelectItem>
                  {team.map((t) => (
                    <SelectItem key={t.team_member_id} value={t.team_member_id}>
                      {t.first_name} {t.last_name}{t.title ? ` · ${t.title}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-12 md:col-span-6">
              <Label className="label-overline">Client sign-off</Label>
              <Select value={form.signoff_user_id || "_none"}
                onValueChange={(v) => set("signoff_user_id", v === "_none" ? "" : v)}
                disabled={!isAdmin}>
                <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="activity-signoff">
                  <SelectValue placeholder="Pick a client contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Not required —</SelectItem>
                  {contacts.map((c) => (
                    <SelectItem key={c.user_id} value={c.user_id}>
                      {c.name || c.email}{c.title ? ` · ${c.title}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="label-overline">Brief / Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
              disabled={!isAdmin}
              className="mt-1 rounded-sm text-sm"
              rows={3}
              placeholder="Internal brief — context, angle, success metric…"
              data-testid="activity-description" />
          </div>

          {/* Reference Material — inspiration assets (small files) + external
              links to large videos hosted on Drive / OneDrive / Dropbox etc.
              Available only after the activity has been saved at least once. */}
          {!isNew && (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50/40 p-3 space-y-2"
              data-testid="reference-material-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="inline-flex items-center gap-1.5">
                  <span className="label-overline">Reference material</span>
                  <span className="text-[10px] font-mono text-zinc-500">
                    inspiration for the team or AI
                  </span>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] font-mono text-[#002FA7] hover:underline cursor-pointer
                      inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#002FA7]/20 bg-white">
                      <input type="file" multiple className="hidden"
                        onChange={(e) => onUploadFiles(e.target.files, "reference")}
                        data-testid="reference-file-input" />
                      {uploadingRef ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      Upload logo / image
                    </label>
                    <button type="button"
                      onClick={() => setShowAddLink((s) => !s)}
                      className="text-[10px] font-mono text-[#002FA7] hover:underline
                        inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#002FA7]/20 bg-white"
                      data-testid="reference-add-link-btn">
                      <ExternalLink2 className="h-3 w-3" /> Add link
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 leading-snug">
                Drop logos / sample images here, and use the link button for
                anything large — paste a Google Drive, OneDrive, SharePoint or
                Dropbox URL so we don&apos;t have to host the video file
                ourselves.
              </p>
              {showAddLink && isAdmin && (
                <div className="rounded-sm border border-[#002FA7]/20 bg-white p-2 space-y-1.5"
                  data-testid="reference-add-link-form">
                  <Input value={newLinkUrl}
                    onChange={(e) => setNewLinkUrl(e.target.value)}
                    placeholder="https://drive.google.com/file/d/…"
                    className="rounded-sm h-8 text-xs font-mono"
                    data-testid="reference-link-url" />
                  <Input value={newLinkTitle}
                    onChange={(e) => setNewLinkTitle(e.target.value)}
                    placeholder="Title (optional)"
                    className="rounded-sm h-8 text-xs"
                    data-testid="reference-link-title" />
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" onClick={addRefLink}
                      className="rounded-sm h-7 bg-[#002FA7] hover:bg-[#001f70] text-white"
                      data-testid="reference-link-save">
                      Add
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-sm h-7"
                      onClick={() => { setShowAddLink(false); setNewLinkUrl(""); setNewLinkTitle(""); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {(() => {
                const refFiles = files.filter((f) => f.kind === "reference");
                const total = refFiles.length + refLinks.length;
                if (total === 0) {
                  return (
                    <div className="text-[11px] text-zinc-400 italic">
                      No reference material yet.
                    </div>
                  );
                }
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {refFiles.map((f) => (
                      <FileRow key={f.file_id} f={f}
                        activityId={activity?.activity_id}
                        onRemove={isAdmin ? () => removeFile(f.file_id) : null} />
                    ))}
                    {refLinks.map((l) => (
                      <ReferenceLinkRow key={l.link_id} link={l}
                        onRemove={isAdmin ? () => removeRefLink(l.link_id) : null} />
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          <div>
            <Label className="label-overline">Post copy / content to publish</Label>
            <Textarea value={form.post_content} onChange={(e) => set("post_content", e.target.value)}
              disabled={!isAdmin}
              className="mt-1 rounded-sm text-sm font-mono"
              rows={5}
              placeholder="The actual text that will be posted on the platform."
              data-testid="activity-post-content" />
          </div>

          {/* Final assets — these are the polished, ready-to-publish files
              (different from Reference Material above, which is inspiration). */}
          <div>
            <Label className="label-overline flex items-center justify-between">
              <span>Final assets</span>
              {isAdmin && !isNew && (
                <label className="text-[10px] font-mono text-[#002FA7] hover:underline cursor-pointer">
                  <input type="file" multiple className="hidden"
                    onChange={(e) => onUploadFiles(e.target.files, "attachment")}
                    data-testid="activity-file-input" />
                  {uploading ? <Loader2 className="h-3 w-3 inline animate-spin" /> : <Upload className="h-3 w-3 inline" />}
                  {" "}Add files
                </label>
              )}
            </Label>
            {isNew && (
              <div className="mt-1 text-[11px] text-zinc-500 italic">
                Save the activity first, then you can attach images and videos.
              </div>
            )}
            {(() => {
              const attachFiles = files.filter((f) => (f.kind || "attachment") === "attachment");
              if (!isNew && attachFiles.length === 0) {
                return (
                  <div className="mt-1 text-[11px] text-zinc-400 italic">
                    No final assets yet.
                  </div>
                );
              }
              if (attachFiles.length === 0) return null;
              return (
                <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {attachFiles.map((f) => (
                    <FileRow key={f.file_id} f={f}
                      activityId={activity?.activity_id}
                      onRemove={isAdmin ? () => removeFile(f.file_id) : null} />
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Lifecycle controls */}
          {!isNew && (
            <div className="border-t border-zinc-100 pt-3">
              <div className="label-overline mb-2">Lifecycle</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {isAdmin && form.status === "planned" && (
                  <Button size="sm" variant="outline" className="rounded-sm h-7"
                    onClick={() => transition("in_progress")}
                    data-testid="transition-in-progress">
                    Start work
                  </Button>
                )}
                {isAdmin && (form.status === "in_progress" || form.status === "planned") && (
                  <Button size="sm" variant="outline" className="rounded-sm h-7"
                    onClick={() => transition("ready_for_signoff")}
                    data-testid="transition-signoff">
                    Send for sign-off
                  </Button>
                )}
                {clientCanSignOff && (
                  <>
                    <Button size="sm" className="rounded-sm h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => transition("approved")}
                      data-testid="transition-approve">
                      <Check className="h-3 w-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline"
                      className="rounded-sm h-7 text-purple-700 border-purple-200 hover:bg-purple-50"
                      onClick={() => {
                        const note = window.prompt(
                          "What would you like changed? (your note will be saved with the activity):",
                        );
                        if (!note) return;
                        transition("revise", note);
                      }}
                      data-testid="transition-revise">
                      Request revise
                    </Button>
                    <Button size="sm" variant="outline"
                      className="rounded-sm h-7 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => {
                        const note = window.prompt("Reason for rejection (optional):") || null;
                        transition("rejected", note);
                      }}
                      data-testid="transition-reject">
                      <X className="h-3 w-3 mr-1" /> Reject
                    </Button>
                  </>
                )}
                {isAdmin && form.status === "approved" && (
                  <Button size="sm" className="rounded-sm h-7 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white"
                    onClick={() => transition("posted")}
                    data-testid="transition-posted">
                    Mark as posted
                  </Button>
                )}
                {isAdmin && form.status === "ready_for_signoff" && (
                  <>
                    <Button size="sm" className="rounded-sm h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => transition("approved")}
                      data-testid="transition-admin-approve">
                      <Check className="h-3 w-3 mr-1" /> Approve on client&apos;s behalf
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7"
                      onClick={() => transition("in_progress")}
                      data-testid="transition-back-progress">
                      Back to in progress
                    </Button>
                  </>
                )}
                {isAdmin && form.status === "revise" && (
                  <Button size="sm" variant="outline" className="rounded-sm h-7"
                    onClick={() => transition("in_progress")}
                    data-testid="transition-rework-from-revise">
                    Start revisions
                  </Button>
                )}
                {isAdmin && form.status === "rejected" && (
                  <Button size="sm" variant="outline" className="rounded-sm h-7"
                    onClick={() => transition("in_progress")}
                    data-testid="transition-rework">
                    Re-open
                  </Button>
                )}
              </div>
              {history.length > 0 && (
                <div className="mt-3 space-y-0.5">
                  {history.slice().reverse().slice(0, 8).map((h, i) => (
                    <div key={`${h.at}-${h.by_name || i}`} className="text-[10px] font-mono text-zinc-500 flex items-center gap-1.5">
                      <Clock className="h-2.5 w-2.5" />
                      {new Date(h.at).toLocaleString("en-AU")} ·{" "}
                      <span className="font-semibold text-zinc-700">{h.by_name || "—"}</span>
                      {h.by_role && (
                        <span className={`text-[9px] px-1 rounded-sm ${h.by_role === "admin" ? "bg-zinc-100 text-zinc-700" : "bg-amber-50 text-amber-700"}`}>
                          {h.by_role}
                        </span>
                      )}
                      · {h.from ? `${h.from} → ${h.to}` : h.to}
                      {h.note && <span className="text-zinc-700"> · &ldquo;{h.note}&rdquo;</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes / discussion thread */}
          {!isNew && (
            <div className="border-t border-zinc-100 pt-3">
              <div className="label-overline mb-2 flex items-center gap-1.5">
                <MessageSquare className="h-3 w-3" /> Notes & comments
                {notes.length > 0 && <span className="text-zinc-400">({notes.length})</span>}
              </div>
              {notes.length === 0 ? (
                <div className="text-[11px] text-zinc-400 italic">
                  No notes yet. Leave a comment or request changes here.
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {notes.map((n) => (
                    <div key={n.note_id} className="bg-zinc-50 rounded-sm p-2"
                      data-testid={`activity-note-${n.note_id}`}>
                      <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 mb-1">
                        <span className="font-semibold text-zinc-700">{n.by_name || "—"}</span>
                        {n.by_role && (
                          <span className={`px-1 rounded-sm ${n.by_role === "admin" ? "bg-zinc-200 text-zinc-700" : "bg-amber-100 text-amber-700"}`}>
                            {n.by_role}
                          </span>
                        )}
                        <span>· {new Date(n.at).toLocaleString("en-AU")}</span>
                      </div>
                      <div className="text-[12px] text-zinc-800 whitespace-pre-wrap">
                        {n.body}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex items-end gap-1.5">
                <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)}
                  placeholder={isAdmin
                    ? "Reply to the client or jot a working note…"
                    : "Suggest a change, leave feedback or ask a question…"}
                  rows={2}
                  className="rounded-sm text-sm flex-1"
                  data-testid="activity-note-input" />
                <Button size="sm" onClick={postNote} disabled={postingNote || !newNote.trim()}
                  className="rounded-sm h-8 bg-[#002FA7] hover:bg-[#001f70] text-white"
                  data-testid="activity-note-post">
                  {postingNote
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : "Post"}
                </Button>
              </div>
            </div>
          )}

          {/* View tracking (audit log of who opened the activity) */}
          {!isNew && views.length > 0 && (
            <div className="border-t border-zinc-100 pt-3">
              <div className="label-overline mb-2 flex items-center gap-1.5">
                <Eye className="h-3 w-3" /> Recent views
                <span className="text-zinc-400">({views.length})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {views.slice().reverse().slice(0, 6).map((v, i) => (
                  <div key={`${v.at}-${v.by_name || i}`} className="text-[10px] font-mono text-zinc-500 flex items-center gap-1.5">
                    <Eye className="h-2.5 w-2.5" />
                    {new Date(v.at).toLocaleString("en-AU")} ·{" "}
                    <span className="text-zinc-700">{v.by_name || "—"}</span>
                    {v.by_role && (
                      <span className={`text-[9px] px-1 rounded-sm ${v.by_role === "admin" ? "bg-zinc-100 text-zinc-700" : "bg-amber-50 text-amber-700"}`}>
                        {v.by_role}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 mt-2">
          {!isNew && isAdmin && (
            <Button variant="outline" onClick={remove}
              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 mr-auto"
              data-testid="activity-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <Button variant="outline" className="rounded-sm" onClick={onClose} disabled={saving}>
            {isAdmin ? "Cancel" : "Close"}
          </Button>
          {isAdmin && (
            <Button onClick={save} disabled={saving}
              className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
              data-testid="activity-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              {isNew
                ? (createMode === "batch"
                    ? `Schedule ${batchPlatforms.size} activit${batchPlatforms.size === 1 ? "y" : "ies"}`
                    : "Schedule activity")
                : "Save changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ContentGuidancePanel({ platform, contentType }) {
  const spec = specFor(platform, contentType);
  if (!spec) {
    return (
      <div className="rounded-sm border border-zinc-200 bg-zinc-50/50 p-3 text-[11px] text-zinc-500 flex items-center gap-2">
        <Info className="h-3 w-3" />
        Pick a platform and content type to see asset requirements and best
        practices for this format.
      </div>
    );
  }
  const platformMeta = PLATFORMS.find((p) => p.key === platform);
  const ctLabel = (contentTypesFor(platform).find((t) => t.key === contentType) || {}).label;
  return (
    <div className="rounded-sm border border-[#002FA7]/15 bg-[#002FA7]/[0.04] p-3"
      data-testid="content-guidance-panel">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <PlatformIcon platform={platform} size={16} />
          <span className="text-xs font-display font-bold text-zinc-900">
            {platformMeta?.label} · {ctLabel}
          </span>
          <span className="label-overline text-[#002FA7] opacity-70">
            Content guidance
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 text-[11px]">
        <GuidanceList title="Media" items={spec.media} accent="#002FA7" />
        <GuidanceList title="Copy" items={spec.copy} accent="#0EA371" />
        <GuidanceList title="Best practice" items={spec.tips} accent="#A16207" />
      </div>
    </div>
  );
}


function GuidanceList({ title, items, accent }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="label-overline mb-1" style={{ color: accent }}>{title}</div>
      <ul className="space-y-1 list-none">
        {items.map((t, i) => (
          <li key={`${t}-${i}`} className="flex items-start gap-1.5 text-zinc-700 leading-snug">
            <span className="text-[8px] mt-1.5 shrink-0" style={{ color: accent }}>●</span>
            <span className="text-[11px]">{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// Per-provider tint + label for reference link cards.
const PROVIDER_META = {
  google_drive: { label: "Google Drive", color: "#4285F4" },
  onedrive:     { label: "OneDrive",     color: "#0078D4" },
  sharepoint:   { label: "SharePoint",   color: "#038387" },
  dropbox:      { label: "Dropbox",      color: "#0061FF" },
  youtube:      { label: "YouTube",      color: "#FF0000" },
  vimeo:        { label: "Vimeo",        color: "#1AB7EA" },
  wetransfer:   { label: "WeTransfer",   color: "#409FFF" },
  figma:        { label: "Figma",        color: "#A259FF" },
  notion:       { label: "Notion",       color: "#000000" },
  link:         { label: "External link", color: "#71717A" },
};

function ReferenceLinkRow({ link, onRemove }) {
  const meta = PROVIDER_META[link.provider] || PROVIDER_META.link;
  return (
    <div className="border border-zinc-200 rounded-sm p-1.5 flex items-center gap-2 bg-white">
      <div className="h-10 w-10 rounded-sm grid place-items-center text-white shrink-0"
        style={{ background: meta.color }}>
        <ExternalLink2 className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <a href={link.url} target="_blank" rel="noreferrer"
          className="text-xs truncate block hover:underline font-display font-semibold">
          {link.title}
        </a>
        <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-1 truncate">
          <span style={{ color: meta.color }}>{meta.label}</span>
          <span>·</span>
          <span className="truncate">{link.url}</span>
        </div>
      </div>
      {onRemove && (
        <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600 px-2"
          onClick={onRemove}>
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}


function FileRow({ f, activityId, onRemove }) {
  const isImage = (f.mime || "").startsWith("image/");
  const url = activityId
    ? `${API}/calendar/activities/${activityId}/files/${f.file_id}`
    : "#";
  return (
    <div className="border border-zinc-200 rounded-sm p-1.5 flex items-center gap-2 bg-zinc-50/40">
      {isImage ? (
        <img src={url} alt="" className="h-10 w-10 object-cover rounded-sm border border-zinc-200" />
      ) : (
        <div className="h-10 w-10 rounded-sm grid place-items-center bg-zinc-100 text-zinc-500">
          <Paperclip className="h-4 w-4" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <a href={url} target="_blank" rel="noreferrer"
          className="text-xs truncate block hover:underline">
          {f.filename}
        </a>
        <div className="text-[10px] font-mono text-zinc-500">
          {f.mime} · {Math.round((f.size || 0) / 1024)} KB
        </div>
      </div>
      {onRemove && (
        <Button size="sm" variant="ghost" className="rounded-sm h-7 text-red-600 px-2"
          onClick={onRemove}>
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
